import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Slider } from "primereact/slider";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LOCALE_BY_CURRENCY, CURRENCY } from "../../../constants/currency";
import KEYBOARD from "../../../constants/keyboard";
import { LOCALES } from "../../../constants/locales";
import { formatAbbrevCurrency } from "../../../helpers/currency";
import { HEADER_INDEX } from "../../../constants/app-constants";
const DropdownRangeSlider = forwardRef(({ props, onChange, selectedPriceState }, ref) => {
  const [minValue, setMinValue] = useState(props.min);
  const [maxValue, setMaxValue] = useState(props.max);
  const [rangeValue, setRangeValue] = useState([props.min, props.max]);
  const openDropdown = useRef(false);
  const [showLabel, setShowLabel] = useState(selectedPriceState !== null);
  const inputRef = useRef(null);

  const ratesData = props.ratesData;
  const defaultCurrency = props.defaultCurrency;

  useImperativeHandle(ref, () => ({
    handleClear,
  }));

  const handleInputClick = (e) => {
    openDropdown.current.toggle(e);
  };

  const convertCurrencyToDouble = (currency) => {
    const currencyCode = props.currency;

    const cleanedValue = currency.replace(/[₫$₩\s]/g, "");

    let numericValue = "";

    if (currencyCode === CURRENCY.VND) {
      numericValue = cleanedValue.replace(/\./g, "");
    } else if (currencyCode === CURRENCY.USD) {
      numericValue = cleanedValue.replace(/,/g, "");
    } else if (currencyCode === CURRENCY.KRW) {
      numericValue = cleanedValue.replace(/,/g, "");
    } else {
      numericValue = cleanedValue.replace(/[^\d.-]/g, "");
    }

    const doubleValue = numericValue.length > 0 ? parseFloat(numericValue) : 0;
    return doubleValue;
  };

  const handleKeyPress = (e, isMin) => {
    if (e.key === KEYBOARD.ENTER) {
      const val = convertCurrencyToDouble(e.target.value);
      isMin ? setMinSafe(val) : setMaxSafe(val);
    }
  };

  const onRangeChange = (e) => {
    const [a, b] = e.value[0] < e.value[1] ? e.value : [e.value[1], e.value[0]];
    setMinValue(a);
    setMaxValue(b);
    setRangeValue([a, b]);
  };


  const onInputChange = (e, isMin) => {
    const val = e.value ?? 0;
    isMin ? setMinSafe(val) : setMaxSafe(val);
  };

  const onBlurChange = (e, isMin) => {
    const val = convertCurrencyToDouble(e.target.value);
    isMin ? setMinSafe(val) : setMaxSafe(val);
  };

  const handleClear = () => {
    setMinValue(props.min);
    setMaxValue(props.max);
    setRangeValue([props.min, props.max]);
    setShowLabel(false);
    onChange(null);
  };

  const { locale } = LOCALE_BY_CURRENCY[props.currency] || { locale: LOCALES.ENUS };

  const renderLabel = () => {
    if (!selectedPriceState) return "";
    if (!props.currency || !ratesData || !defaultCurrency) return "";

    const [v0, v1] = rangeValue;
    return formatAbbrevCurrency(v0, props.currency, ratesData, props.currency) + " – " + formatAbbrevCurrency(v1, props.currency, ratesData, props.currency);
  };

  const clamp = (val) => Math.min(props.max, Math.max(props.min, val));

  const setMinSafe = (val) => {
    const newMin = clamp(val);
    setMinValue(newMin);
    setMaxValue((prev) => (prev < newMin ? newMin : prev));
  };

  const setMaxSafe = (val) => {
    const newMax = clamp(val);
    setMaxValue(newMax);
    setMinValue((prev) => (prev > newMax ? newMax : prev));
  };


  useEffect(() => {
    const hasValue = selectedPriceState != null;
    setShowLabel(hasValue);
    if (hasValue) {
      setMinValue(selectedPriceState.min);
      setMaxValue(selectedPriceState.max);
      setRangeValue([selectedPriceState.min, selectedPriceState.max]);
    } else {
      setMinValue(props.min);
      setMaxValue(props.max);
      setRangeValue([props.min, props.max]);
    }
  }, [selectedPriceState, props.min, props.max]);

  const handleOverlayShow = () => {
    if (openDropdown.current) {
      const overlayElement = openDropdown.current.getElement();
      if (overlayElement) {
        const sidebar = document.querySelector(".p-sidebar-left");
        if (sidebar) {
          overlayElement.style.zIndex = `${HEADER_INDEX + 2}`;
        } else {
          overlayElement.style.zIndex = `${HEADER_INDEX - 1}`;
        }
    }
    }
  };

  const handleOverlayHide = () => {
    onChange({ min: minValue, max: maxValue });
    if (openDropdown.current) {
      const overlayElement = openDropdown.current.getElement();
      if (overlayElement) {
        overlayElement.style.zIndex = '';
      }
    }
  };

  return (
    <div className="relative">
      <div ref={inputRef} className="relative w-full flex custom-dropdown-container">
        <InputText
          className="keduall-form dropdown-input p-dropdown z-1 keduall-typography text-default-txt keduall-typography text-16-semibold-600 custom-placeholder"
          type="text"
          value={renderLabel()}
          onClick={handleInputClick}
          readOnly
          placeholder={`${formatAbbrevCurrency(0, props.currency, ratesData, props.currency) + " – " + formatAbbrevCurrency(0, props.currency, ratesData, props.currency)}`}
        />
        {showLabel && <Button icon="pi pi-times z-5 flex" className="p-button-text absolute keduall-form dropdown-input icon icon-times-circle" onClick={handleClear} />}
        <i className="pi cursor-pointer pi-angle-down keduall-form dropdown-input icon-angle-down-small absolute z-4 flex" onClick={handleInputClick}></i>
      </div>

      <OverlayPanel onShow={handleOverlayShow} onHide={handleOverlayHide} ref={openDropdown} appendTo={inputRef.current} className="left-0 top-100 w-webkit-fill-available mt-2">
        <div className="p-dropdown-panel keduall-form dropdown-input w-webkit-fill-available p-dropdown flex flex-column gap-075">
          <div className="w-full flex keduall-typography text-16-bold-700 mt-2 mb-1 text-default-txt">{props?.rangeHeader}</div>
          <div className="flex flex-row justify-content-center align-items-center gap-075 w-full">
            <div className="flex flex-column w-full">
              <label className="flex keduall-typography text-16-normal-400 text-default-txt mb-1">{props?.minHeader}: </label>
              <InputNumber
                inputClassName="flex keduall-form input-default-default keduall-buttons share-2-btn-row panel-input-custom-placeholder keduall-typography text-16-normal-400 text-default-txt"
                value={minValue}
                className="w-full"
                placeholder={props?.placeholder}
                mode="currency"
                currency={props?.currency}
                locale={locale}
                onChange={(e) => onInputChange(e, true)}
                onBlur={(e) => onBlurChange(e, true)}
                onKeyDown={(e) => handleKeyPress(e, true)}
              />
            </div>
            <h5 className="flex keduall-typography heading-h5-normal-400 mt-4">&#126;</h5>
            <div className="flex flex-column w-full">
              <label className="flex keduall-typography text-16-normal-400 text-default-txt mb-1">{props?.maxHeader}: </label>
              <InputNumber
                inputClassName="flex keduall-form input-default-default keduall-buttons share-2-btn-row panel-input-custom-placeholder keduall-typography text-16-normal-400 text-default-txt"
                value={maxValue}
                onChange={(e) => onInputChange(e, false)}
                onBlur={(e) => onBlurChange(e, false)}
                onKeyDown={(e) => handleKeyPress(e, false)}
                className="w-full"
                mode="currency"
                placeholder={props?.placeholder}
                currency={props?.currency}
                locale={locale}
              />
            </div>
          </div>
          <div className="p-075 w-full flex flex-column mb-3">
            <Slider className="flex keduall-slider slider-default" value={rangeValue} range min={props?.min} max={props?.max} step={props?.step} onChange={onRangeChange} />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
});

export default DropdownRangeSlider;
